import { RotateLeft, RotateRight } from '@mui/icons-material';
import { Box, Button, IconButton, Stack, SxProps, Theme } from '@mui/material';
import { useEffect, useState } from 'react';
import { rotateImageAndUpload } from '../../utils/imageRotation';

interface RotatableImageProps {
  src: string;
  alt?: string;
  folderType: number;
  onRotated?: (newUrl: string) => void;
  onSaving?: (saving: boolean) => void;
  imageStyle?: React.CSSProperties;
  containerSx?: SxProps<Theme>;
  showApplyButton?: boolean;
}

export const RotatableImage: React.FC<RotatableImageProps> = ({
  src,
  alt = 'Image preview',
  folderType,
  onRotated,
  onSaving,
  imageStyle,
  containerSx,
  showApplyButton = true,
}) => {
  const [previewRotation, setPreviewRotation] = useState(0);
  const [currentSrc, setCurrentSrc] = useState(src);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setCurrentSrc(src);
    setPreviewRotation(0);
  }, [src]);

  const handleRotate = (direction: 'left' | 'right') => {
    setPreviewRotation((prev) =>
      direction === 'right' ? prev + 90 : prev - 90,
    );
  };

  const handleApplyRotation = async () => {
    const normalizedRotation =
      ((previewRotation % 360) + 360) % 360;

    if (normalizedRotation === 0) {
      return;
    }

    try {
      setSaving(true);
      onSaving?.(true);

      const newUrl = await rotateImageAndUpload(
        currentSrc,
        normalizedRotation,
        folderType,
      );

      setCurrentSrc(newUrl);
      setPreviewRotation(0);
      onRotated?.(newUrl);
    } catch (error) {
      console.error('Failed to rotate image:', error);
    } finally {
      setSaving(false);
      onSaving?.(false);
    }
  };

  const normalizedPreviewRotation =
    ((previewRotation % 360) + 360) % 360;

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          ...containerSx,
        }}
      >
        <img
          src={currentSrc}
          alt={alt}
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            transition: 'transform 0.2s ease',
            transform: `rotate(${previewRotation}deg)`,
            ...imageStyle,
          }}
        />
      </Box>

      <Stack
        direction="row"
        spacing={1}
        justifyContent="center"
        alignItems="center"
        mt={1.5}
      >
        <IconButton
          onClick={() => handleRotate('left')}
          disabled={saving}
          title="Rotate left"
          sx={{
            color: '#fff',
            backgroundColor: 'rgba(255,255,255,0.1)',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
          }}
        >
          <RotateLeft />
        </IconButton>

        <IconButton
          onClick={() => handleRotate('right')}
          disabled={saving}
          title="Rotate right"
          sx={{
            color: '#fff',
            backgroundColor: 'rgba(255,255,255,0.1)',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
          }}
        >
          <RotateRight />
        </IconButton>

        {showApplyButton && normalizedPreviewRotation !== 0 && (
          <Button
            variant="contained"
            size="small"
            onClick={handleApplyRotation}
            disabled={saving}
            sx={{ textTransform: 'none' }}
          >
            {saving ? 'Applying...' : 'Apply Rotation'}
          </Button>
        )}
      </Stack>
    </Box>
  );
};
